import React from "react";
import styled from "@emotion/styled";
import ReactSelect from "react-select";

const MultiSelect = React.forwardRef(({
  label,
  options,
  value, // Should be an array of values
  onChange,
  placeholder = "Select",
  required = false,
  name,
  className,
  disabled = false,
  error = false,
  errorMessage
}, ref) => {
  // Convert value array to selected options format
  const selectedOptions = Array.isArray(value)
    ? value.map(val => options.find(opt => opt.value === val)).filter(Boolean)
    : [];

  const handleChange = (selected) => {
    // Convert selected options back to array of values
    const values = selected ? selected.map(opt => opt.value) : [];
    onChange?.({ target: { name, value: values } });
  };

  // Custom styles to match the existing Select component
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      height: 'auto',
      backgroundColor: '#ffffff',
      border: error ? 'solid 1px rgb(220, 38, 38)' : 'solid 1px rgb(224, 228, 244)',
      borderRadius: '8px',
      boxShadow: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        borderColor: error ? 'rgb(220, 38, 38)' : 'rgb(97, 98, 197)',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 16px',
      gap: '4px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#000000',
      fontSize: '16px',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      fontSize: '16px',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#F1F5F9',
      borderRadius: '6px',
      padding: '2px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#6162C5',
      fontSize: '14px',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#6162C5',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#E2E8F0',
        color: '#4B4ACB',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
      padding: '8px',
      marginTop: '4px',
      zIndex: 10,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: '300px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#F1F5F9' : state.isFocused ? '#F1F5F9' : 'transparent',
      color: state.isSelected ? '#6162C5' : '#000000',
      fontSize: '16px',
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 500,
      padding: '12px 16px',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      '&:active': {
        backgroundColor: '#F1F5F9',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#64748B',
      padding: '8px',
      '&:hover': {
        color: '#64748B',
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#64748B',
      padding: '8px',
      cursor: 'pointer',
      '&:hover': {
        color: '#4B4ACB',
      },
    }),
  };

  return (
    <SelectContainer className={className} ref={ref}>
      <Label>
        {label} {required && <Required>*</Required>}
      </Label>
      <ReactSelect
        isMulti
        name={name}
        value={selectedOptions}
        onChange={handleChange}
        options={options}
        placeholder={placeholder}
        isDisabled={disabled}
        styles={customStyles}
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
      />
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </SelectContainer>
  );
});

MultiSelect.displayName = 'MultiSelect';

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: normal;
  text-align: left;
`;

const Required = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-weight: 700;
  margin-left: 4px;
`;

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
  text-align: start;
`;

export default MultiSelect;
